import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from 'src/environments/environment';

declare global {
  interface Window {
    google?: any;
  }
}

@Component({
  selector: 'app-google-login-button',
  templateUrl: './google-login-button.component.html',
  styleUrls: ['./google-login-button.component.scss'],
  standalone: false,
})
export class GoogleLoginButtonComponent implements AfterViewInit, OnDestroy {
  @Input() width = 320;
  @Input() label = 'global.login_with_google';
  @Output() credential = new EventEmitter<string>();
  @Output() failed = new EventEmitter<void>();
  @ViewChild('googleBtn', { static: true }) googleBtn: ElementRef<HTMLElement>;

  showFallback = false;

  private retries = 0;
  private readonly maxRetries = 20;
  private retryTimer: ReturnType<typeof setTimeout>;

  constructor(
    private ngZone: NgZone,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  ngAfterViewInit(): void {
    // Google Identity Services only exists in the browser; skip during SSR.
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.renderWhenLoaded();
  }

  private renderWhenLoaded(): void {
    const google = window.google;
    if (google?.accounts?.id) {
      try {
        google.accounts.id.initialize({
          client_id: environment.googleClientId,
          callback: (response: { credential: string }) => {
            // GSI fires the callback outside Angular's zone.
            this.ngZone.run(() => this.credential.emit(response.credential));
          },
        });
        google.accounts.id.renderButton(this.googleBtn.nativeElement, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          logo_alignment: 'center',
          width: this.width,
        });
      } catch {
        this.showError();
      }
      return;
    }

    if (this.retries >= this.maxRetries) {
      this.showError();
      return;
    }
    this.retries++;
    this.retryTimer = setTimeout(() => this.renderWhenLoaded(), 300);
  }

  private showError(): void {
    this.ngZone.run(() => {
      this.showFallback = true;
      this.failed.emit();
    });
  }

  ngOnDestroy(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
    }
  }
}
